import React, { useState, useEffect, useRef } from "react";
import CodeBlock from "./CodeBlock";
import { Endpoint as EndpointType } from "../../types/apiTypes";
import {
  apiCodeSnippets,
  availableSlideTypes,
  generateRequestBody,
  generateDynamicCustomSlideSnippets,
} from "../../utils/apiConfig";
import SlideTypeDropdown from "./SlideTypeDropdown";

type HttpMethod = "POST" | "GET" | "PUT" | "DELETE";

const MethodBadge: React.FC<{ method: HttpMethod }> = ({ method }) => {
  const colors: Record<HttpMethod, string> = {
    POST: "bg-blue-500 border-blue-500 text-white",
    GET: "bg-green-500 border-green-500 text-white",
    PUT: "bg-yellow-500 border-yellow-500 text-white",
    DELETE: "bg-red-500 border-red-500 text-white",
  };
  return (
    <span
      className={`inline-block px-2.5 py-0.5 text-xs font-semibold rounded-md border ${colors[method]}`}
    >
      {method}
    </span>
  );
};

interface ParameterTableProps {
  params: {
    name: string;
    type: string;
    description: string;
    optional: boolean;
  }[];
}
const ParameterTable: React.FC<ParameterTableProps> = ({ params }) => (
  <div className="w-full mt-4 border border-slate-200 rounded-lg overflow-hidden">
    <table className="w-full table-auto divide-y divide-slate-200">
      <thead className="bg-slate-50">
        <tr>
          <th className="w-1/5 px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
            Name
          </th>
          <th className="w-1/5 px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
            Type
          </th>
          <th className="w-2/5 px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
            Description
          </th>
          <th className="w-1/5 px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
            Mandatory
          </th>
        </tr>
      </thead>

      <tbody className="bg-white divide-y divide-slate-200">
        {params.map((param) => (
          <tr key={param.name}>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-slate-700">
              {param.name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-slate-500">
              {param.type}
            </td>
            <td className="px-6 py-4 text-sm text-slate-600">
              {param.description}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 text-center">
              {param.optional ? "❌" : "✅"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

interface ApiEndpointProps {
  endpoint: EndpointType;
}

const ApiEndpoint: React.FC<ApiEndpointProps> = ({ endpoint }) => {
  const [selectedSlideType, setSelectedSlideType] = useState<string>("Points");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [videoSrc, setVideoSrc] = useState("");
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Detect screen size and set the appropriate video source
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setVideoSrc(
          "https://d2zu6flr7wd65l.cloudfront.net/uploads/Copy+of+17th+Feb+New+Zynth+Demo+Mobile+Video.mp4"
        );
      } else {
        setVideoSrc("");
      }
    };

    handleResize(); // Run on mount
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (isModalOpen && videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.error("Video play failed:", error);
      });
    }
  }, [isModalOpen]);

  const closeModal = (e: React.MouseEvent) => {
    e.stopPropagation(); // Stop event bubbling
    setIsModalOpen(false);
  };

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent click events on the modal from closing it
  };

  const handleWatchDemo = (endpointId: string) => {
    
    if (endpointId === "generate-presentation") {
      return; 
    }

    
    if (window.innerWidth <= 768) {
      setVideoSrc(
        "https://d2zu6flr7wd65l.cloudfront.net/uploads/Copy+of+17th+Feb+New+Zynth+Demo+Mobile+Video.mp4"
      );
    } else {
   
      switch (endpointId) {
        case "generate-custom-slide":
          setVideoSrc(
            "https://d2zu6flr7wd65l.cloudfront.net/uploads/Custom+Builder+Slide+API+Demo.mp4"
          );
          break;
        case "generate-slide-narrative":
          setVideoSrc(
            "https://d2zu6flr7wd65l.cloudfront.net/uploads/Slide+Narrative+Demo.mp4"
          );
          break;
        default:
          setVideoSrc("");
      }
    }
    setIsModalOpen(true);
  };

  const handleSlideTypeChange = (newType: string) => {
    setSelectedSlideType(newType);
  };

  const renderEndpointSection = (data: any, isMode: boolean = false) => {
    const isGenerateCustomSlide =
      endpoint.id === "generate-custom-slide" && !isMode;

    const requestData = isGenerateCustomSlide
      ? generateRequestBody(selectedSlideType)
      : data.request;

    return (
      <div
        id={data.id}
        className={isMode ? "mt-8 pt-8 border-t border-slate-200" : ""}
      >
        {isGenerateCustomSlide && (
          <SlideTypeDropdown
            selectedType={selectedSlideType}
            onTypeChange={handleSlideTypeChange}
            slideTypes={availableSlideTypes}
          />
        )}
        {data.request && (
          <details className="mt-6">
            <summary
              className="text-lg font-semibold text-slate-700 cursor-pointer hover:text-slate-900 scroll-mt-20"
              id={`${endpoint.id}${isMode ? `-${data.id}` : ""}-request-body`}
            >
              Request Body
            </summary>
            <div className="mt-4">
              <CodeBlock title="Request Body" code={requestData} />
            </div>
          </details>
        )}

        {endpoint.id === "generate-custom-slide" && !isMode && (
          <details className="mt-8">
            <summary className="text-lg font-semibold text-slate-700 cursor-pointer hover:text-slate-900 mb-4">
              Minimum and Maximum Data Requirements by Slide Type
            </summary>
            <div className="w-full border border-slate-200 rounded-lg overflow-hidden mt-4">
              <table className="w-full table-auto divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="w-1/3 px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Slide Type
                    </th>
                    <th className="w-1/3 px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Minimum
                    </th>
                    <th className="w-1/3 px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Maximum
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                      TextplusImage
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      1
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      1
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                      Points
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      2
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      6
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                      Timeline/Phases
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      3
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      6
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                      Images
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      1
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      4
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                      Tables
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      2 rows x 2 columns
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      8 rows x 5 columns
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                      People
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      2
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      6
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                      Charts/Graphs
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      2 data series with 2 entries
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      3 data series with 10 entries
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                      Statistics
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      2
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      4
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </details>
        )}

        {data.response && (
          <details className="mt-6">
            <summary
              className="text-lg font-semibold text-slate-700 cursor-pointer hover:text-slate-900 scroll-mt-20"
              id={`${endpoint.id}${isMode ? `-${data.id}` : ""}-response-body`}
            >
              Response Body
            </summary>
            <div className="mt-4">
              <CodeBlock title="Response Body" code={data.response} />
              <div className="mt-4 p-4 bg-slate-50 rounded-md">
                <p className="text-sm text-slate-700">
                  <span className="font-semibold">Url:</span> The url is the
                  link for the presentation. In case the{" "}
                  {endpoint.id === "generate-presentation" ? "slides" : "slide"}{" "}
                  {endpoint.id === "generate-presentation" ? "are" : "is"} not
                  visible, please wait for a few seconds for the{" "}
                  {endpoint.id === "generate-presentation" ? "slides" : "slide"}{" "}
                  to complete after receiving the presentation URL in the API
                  response.
                </p>
                {endpoint.id === "generate-presentation" && (
                  <p className="text-sm text-slate-700 mt-2">
                    <span className="font-semibold">slideCount:</span> The
                    number of slides expected in the presentation.
                  </p>
                )}
              </div>
            </div>
          </details>
        )}
      </div>
    );
  };

  // State for API examples tab
  const [activeTab, setActiveTab] = useState<
    "curl" | "http" | "json" | "javascript"
  >("curl");

  // Add API examples section for each endpoint
  const renderApiExamples = (endpointId: string) => {
    const languages: ("curl" | "http" | "json" | "javascript")[] = [
      "curl",
      "http",
      "json",
      "javascript",
    ];

    // Use dynamic snippets for generate-custom-slide endpoint, otherwise use static snippets
    const snippets =
      endpointId === "generate-custom-slide"
        ? generateDynamicCustomSlideSnippets(selectedSlideType)
        : apiCodeSnippets[endpointId];

    if (!snippets) return null;

    return (
      <div className="scroll-mt-20" id={`${endpointId}-examples`}>
        <div className="w-full bg-white shadow rounded-md border border-slate-200">
          <div className="flex space-x-4 border-b p-4">
            {languages.map((lang) => (
              <button
                key={lang}
                className={`py-2 px-4 text-sm font-medium capitalize ${
                  activeTab === lang
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-500 hover:text-blue-500"
                }`}
                onClick={() => setActiveTab(lang)}
              >
                {lang}
              </button>
            ))}
          </div>
          <pre className="bg-[#3667B2] text-white text-sm rounded-b-md p-4 overflow-x-auto">
            <code>{snippets[activeTab]}</code>
          </pre>
        </div>
      </div>
    );
  };

  return (
    <article id={endpoint.id} className="mb-16 scroll-mt-20">
      <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
        {endpoint.title}
      </h2>
      <p className="mt-2 text-md text-slate-600">{endpoint.description}</p>

      {/* Add endpoint path at the top */}
      {endpoint.method && endpoint.path && (
        <div className="flex items-center space-x-3 mt-4 bg-slate-100 p-3 rounded-md">
          <MethodBadge method={endpoint.method} />
          <span className="text-md font-mono text-slate-700">
            {endpoint.path}
          </span>
        </div>
      )}

      {endpoint.headers && endpoint.headers.length > 0 && (
        <details className="mt-6">
          <summary
            className="text-lg font-semibold text-slate-700 cursor-pointer hover:text-slate-900 scroll-mt-20"
            id={`${endpoint.id}-headers`}
          >
            Headers
          </summary>
          <div className="mt-4">
            <ParameterTable
              params={endpoint.headers.map((h) => ({
                name: h.name,
                type: "string",
                description: h.value,
                optional: false,
              }))}
            />
          </div>
        </details>
      )}
      {endpoint.bodyParams && endpoint.bodyParams.length > 0 && (
        <details className="mt-6">
          <summary
            className="text-lg font-semibold text-slate-700 cursor-pointer hover:text-slate-900 scroll-mt-20"
            id={`${endpoint.id}-body-parameters`}
          >
            Body Parameters
          </summary>
          <div className="mt-4">
            {/* Add note for slide generation endpoints */}
            {(endpoint.id === "generate-custom-slide" ||
              endpoint.id === "generate-slide-narrative") && (
              <div className="mb-4 p-4 bg-blue-50 border-l-4 border-blue-400 rounded-r-md">
                <p className="text-blue-800 text-sm">
                  <strong>📋 Note:</strong> For detailed explanations of common
                  parameters and their behaviors (especially{" "}
                  <code className="bg-blue-100 px-1 py-0.5 rounded">
                    presentationID
                  </code>
                  ), please refer to the{" "}
                  <a
                    href="#parameter-behavior"
                    className="text-blue-600 hover:underline font-medium"
                  >
                    Parameter Behavior
                  </a>{" "}
                  section below.
                </p>
              </div>
            )}
            <ParameterTable params={endpoint.bodyParams} />
          </div>
        </details>
      )}

      {endpoint.method && renderEndpointSection(endpoint)}

      {endpoint.modes &&
        endpoint.modes.length > 0 &&
        endpoint.modes.map((mode) => renderEndpointSection(mode, true))}

      {/* Add API examples for this endpoint */}
      <details className="mt-8">
        <summary className="text-xl font-semibold text-slate-800 cursor-pointer hover:text-slate-900">
          API Request Examples
        </summary>
        <div className="mt-4">{renderApiExamples(endpoint.id)}</div>
      </details>

      {/* Add Demo section for this endpoint */}
      <details className="mt-8">
        <summary
          className="text-xl font-semibold text-slate-800 cursor-pointer hover:text-slate-900 scroll-mt-20"
          id={`${endpoint.id}-demo`}
          onClick={(e) => {
            e.preventDefault();
            handleWatchDemo(endpoint.id);
          }}
        >
          Watch Demo
        </summary>
      </details>

      {/* Video Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-lg shadow-lg w-[90%] md:w-[60%] lg:w-[80%] lg:h-[80%] xl:w-[90%] xl:h-[85%] relative flex items-center justify-center"
            onClick={stopPropagation}
          >
            <button
              onClick={closeModal}
              className="absolute top-2 right-2 text-gray-700 hover:text-gray-900 text-2xl font-bold z-50"
            >
              &times;
            </button>
            {videoSrc ? (
              <video
                ref={videoRef}
                controls
                className="w-full h-full rounded-md object-contain z-10"
              >
                <source src={videoSrc} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-500">
                <p>Loading video...</p>
              </div>
            )}
          </div>
        </div>
      )}
    </article>
  );
};

export default ApiEndpoint;
